import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import Spinner from "../components/spinner";
import { stripHtmlTags } from "../utils/stripHtmlTags";
import renderPaginationButtons from "../components/pagination";
import portalsApi from "../api/portalsApi";
import categoriesApi from "../api/categoriesApi";
import LikeButton from "../components/likeButton";
import { getTimeAgo } from "../utils/getTimeAgo";
import ImageWithFallback from "../helpers/ImageWithFallback";
import PortalLogoFallback from "../helpers/PortalLogoFallback";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentAlt } from "@fortawesome/free-solid-svg-icons";
import { IoMdAddCircle, IoMdRemoveCircle } from "react-icons/io";
import { IoImageOutline } from "react-icons/io5";

// Cache management functions
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes in milliseconds

const getCachedData = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const { data, timestamp } = JSON.parse(item);
    if (Date.now() - timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch (error) {
    return null;
  }
};

const setCachedData = (key, data) => {
  try {
    const item = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error("Error caching data:", error);
  }
};

// Function to manage recently visited portals
const updateRecentPortals = (portal) => {
  try {
    const recentPortals = JSON.parse(
      localStorage.getItem("recentPortals") || "[]"
    );
    // Remove if portal already exists
    const filteredPortals = recentPortals.filter((p) => p.id !== portal.id);
    // Add portal to start of array
    filteredPortals.unshift(portal);
    // Keep only last 5 visited portals
    const updatedPortals = filteredPortals.slice(0, 5);
    localStorage.setItem("recentPortals", JSON.stringify(updatedPortals));
  } catch (error) {
    console.error("Error updating recent portals:", error);
  }
};

const sanitizePortalName = (name) => {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[ëę]/g, "e") // Handle special characters
    .replace(/[áàâä]/g, "a")
    .replace(/[íìîï]/g, "i")
    .replace(/[óòôö]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/[ç]/g, "c")
    .replace(/[^a-z0-9-]/g, "") // Remove any remaining special chars
    .replace(/-+/g, "-") // Replace multiple hyphens with single
    .trim(); // Trim hyphens from start/end
};

const PortalArticlePage = ({ onNotFound }) => {
  const { portalName } = useParams();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(18);
  const [totalPages, setTotalPages] = useState(1);

  // First, fetch and validate the portal
  useEffect(() => {
    const fetchPortal = async () => {
      try {
        console.log("Searching for portal:", portalName);
        const sanitizedUrlName = sanitizePortalName(portalName);
        console.log("Sanitized URL name:", sanitizedUrlName);

        // Try to get portal data from cache first
        const cachedPortals = getCachedData("allPortals");
        let portal;

        if (cachedPortals) {
          portal = cachedPortals.find((p) => {
            const sanitizedPortalName = sanitizePortalName(p.name);
            console.log(
              "Comparing:",
              sanitizedPortalName,
              "with:",
              sanitizedUrlName
            );
            return sanitizedPortalName === sanitizedUrlName;
          });
        }

        // If not in cache or cache expired, fetch from API
        if (!portal) {
          console.log("Portal not found in cache, fetching from API");
          const { data } = await portalsApi.getAll();
          setCachedData("allPortals", data);

          portal = data.find((p) => {
            const sanitizedPortalName = sanitizePortalName(p.name);
            console.log(
              "Comparing (API):",
              sanitizedPortalName,
              "with:",
              sanitizedUrlName
            );
            return sanitizedPortalName === sanitizedUrlName;
          });
        }

        if (!portal) {
          console.log("Portal not found:", portalName);
          onNotFound();
          return;
        }

        console.log("Portal found:", portal);
        setPortal(portal);
        updateRecentPortals(portal);
        localStorage.setItem("currentPortal", JSON.stringify(portal));
      } catch (err) {
        console.error("Error fetching portal:", err);
        onNotFound();
      }
    };

    fetchPortal();
  }, [portalName, onNotFound]);

  // Then fetch articles and categories if portal exists
  useEffect(() => {
    const fetchArticlesAndCategories = async () => {
      if (!portal) return;

      try {
        setLoading(true);

        // Try to get cached articles first
        const cacheKey = `portal_${portal.id}_articles_page_${page}`;
        const cachedArticles = getCachedData(cacheKey);

        if (cachedArticles) {
          setArticles(cachedArticles.results);
          setTotalPages(Math.ceil(cachedArticles.count / pageSize));
        } else {
          // Fetch articles if not in cache
          const articlesResponse = await portalsApi.getArticles(
            portal.id,
            page,
            pageSize
          );
          const sortedArticles = articlesResponse.data.results.sort(
            (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
          );
          setArticles(sortedArticles);
          setTotalPages(Math.ceil(articlesResponse.data.count / pageSize));
          setCachedData(cacheKey, articlesResponse.data);
        }

        // Try to get cached categories
        const cachedCategories = getCachedData("categories");
        if (cachedCategories) {
          setCategories(cachedCategories);
        } else {
          // Fetch categories if not in cache
          const categoriesResponse = await categoriesApi.getAll();
          setCategories(categoriesResponse.data);
          setCachedData("categories", categoriesResponse.data);
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Failed to fetch articles or categories"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchArticlesAndCategories();
  }, [portal, page, pageSize]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "";
  };

  // Handle category click
  const handleCategoryClick = (id, name) => {
    const sanitizedCategoryName = name.toLowerCase().replace(/\s+/g, "-");
    localStorage.setItem("categoryName", sanitizedCategoryName);
    localStorage.setItem("categoryId", id);
    navigate(`/category/${sanitizedCategoryName}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle "Read More" navigation
  const handleReadMore = (article) => {
    if (article?.categories_related?.[0]) {
      localStorage.setItem("categoryId", article.categories_related[0]);
    }
    navigate(`/article/${article.id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center text-red-500 mt-10">
        Error loading articles: {error}
      </p>
    );
  }

  return (
    <section className="container mx-auto px-4 py-10">
      <h2 className="text-4xl font-extrabold text-gray-800 dark:text-gray-200 text-center mb-12">
        Artikujt nga {portal?.name || "Unknown Portal"}
      </h2>

      {articles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => {
            const categoryId = article?.categories_related?.[0];
            const categoryName = getCategoryName(categoryId);
            const commentsCount = article.comments?.length || 0;

            return (
              <div
                key={article.id}
                className="relative bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden transform hover:scale-105 transition duration-300 ease-in-out cursor-pointer"
              >
                <div
                  className="block sm:hidden bg-white  overflow-hidden cursor-pointer"
                  onClick={() => handleReadMore(article)}
                >
                  <div className="relative w-full h-64 overflow-hidden">
                    <ImageWithFallback
                      src={
                        article.thumbnail ||
                        "https://via.placeholder.com/600x400"
                      }
                      className="w-full h-full object-cover"
                      fallback={
                        <IoImageOutline className="w-full h-full object-cover bg-gray-100 text-gray-500" />
                      }
                    />

                    {/* Gradient overlay */}
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 30%, transparent 70%)",
                        pointerEvents: "none",
                      }}
                    ></div>

                    <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
                      <div className="grid grid-cols-3 items-center">
                        <div className="flex justify-start"></div>
                        <div className="flex justify-center">
                          <span className="text-xs text-gray-800 font-semibold">
                            {getTimeAgo(article.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="px-2">
                    <h3 className="text-xl font-bold text-gray-900 mt-2 flex items-start ">
                      <div
                        style={{
                          width: "4px",
                          minWidth: "4px",
                          height: "14px",
                          backgroundColor: "#DC2626",
                          marginRight: "8px",
                          marginTop: "4px",
                          flexShrink: 0,
                        }}
                      ></div>
                      <span className="mb-5">{article.title}</span>
                    </h3>
                    <span className="mb-5 line-clamp-2">
                      {stripHtmlTags(article.content)}
                    </span>
                    <div className="flex justify-end items-center">
                      <div className="flex items-center">
                        <LikeButton
                          articleId={article.id}
                          initialLikeStatus={article.liked}
                          likesCount={article.likes_count}
                        />
                        <FontAwesomeIcon
                          icon={faCommentAlt}
                          className="text-gray-500 mr-2"
                        />
                        <p className="text-sm text-gray-500">{commentsCount}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="hidden sm:block relative bg-white rounded-lg overflow-hidden cursor-pointer rounded-t-none group"
                  onClick={() => handleReadMore(article)}
                >
                  <div className="relative w-full h-64 overflow-hidden">
                    <ImageWithFallback
                      src={
                        article.thumbnail ||
                        "https://via.placeholder.com/600x400"
                      }
                      className="w-full h-full object-cover"
                      fallback={
                        <IoImageOutline className="w-full h-full object-cover bg-gray-100 text-gray-500" />
                      }
                    />

                    {/* Gradient overlay */}
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 30%, transparent 70%)",
                        pointerEvents: "none",
                      }}
                    ></div>

                    <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
                      <div className="grid grid-cols-3 items-center">
                        <div className="flex justify-start"></div>
                        <div className="flex justify-center">
                          <span className="text-xs text-gray-800 font-semibold">
                            {getTimeAgo(article.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="text-2xl sm:text-xl font-bold text-gray-900 flex items-start group-hover:underline transition-all">
                      <div
                        style={{
                          width: "4px",
                          minWidth: "4px",
                          height: "18px",
                          backgroundColor: "#DC2626",
                          marginRight: "8px",
                          marginTop: "4px",
                          flexShrink: 0,
                        }}
                      ></div>
                      <span>{article.title}</span>
                    </h3>
                    <p className="text-gray-700 mb-4 line-clamp-3 text-sm sm:text-base">
                      {stripHtmlTags(article.content)}
                    </p>

                    <div className="flex justify-end items-center">
                      <div className="flex items-center">
                        <LikeButton
                          articleId={article.id}
                          initialLikeStatus={article.liked}
                          likesCount={article.likes_count}
                        />
                        <FontAwesomeIcon
                          icon={faCommentAlt}
                          className="text-gray-500 mr-2"
                        />
                        <p className="text-sm text-gray-500">{commentsCount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-600 dark:text-gray-400 text-center">
          No articles available for this portal.
        </p>
      )}

      <div className="flex justify-center items-center mt-6 space-x-2">
        {renderPaginationButtons({
          totalPages,
          currentPage: page,
          handlePageChange,
        })}
      </div>
    </section>
  );
};

export default PortalArticlePage;